'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { LogsEvent } from '@datadog/browser-logs';
import type { RumEvent } from '@datadog/browser-rum';

const ANALYTICS_ALLOWED_HOSTS = [
  'portal.joinprequel.com',
  'portal.unbound.school',
  'portal.novatio.school',
  'portal.gt.school'
];

function isAllowedAnalyticsHost() {
  if (typeof window === 'undefined') return false;

  return ANALYTICS_ALLOWED_HOSTS.includes(window.location.hostname);
}

function shouldRunAnalytics() {
  const isProductionEnv = process.env.VERCEL_ENV === 'production' || process.env.NODE_ENV === 'production';

  return isProductionEnv && isAllowedAnalyticsHost();
}

export function includesGoguardian(
  value: object | null | undefined,
  field: 'stack' | 'message' | 'url' | 'target'
): boolean {
  if (!value) return false;
  const fields = value as { stack?: unknown; message?: unknown; url?: unknown; target?: unknown };
  const fieldValue = fields[field];
  if (typeof fieldValue === 'string') return fieldValue.toLowerCase().includes('goguardian');
  if (field !== 'target' || typeof fieldValue !== 'object' || fieldValue === null) return false;

  const targetName = (fieldValue as { name?: unknown }).name;
  return typeof targetName === 'string' && targetName.toLowerCase().includes('goguardian');
}

declare global {
  interface Window {
    isDDLogsInitialized?: boolean;
    isDDRumInitialized?: boolean;
  }
}

/**
 * Starts Datadog RUM (with Session Replay) and browser logs once per page load, after sign-in
 * (from `TrackingWithSSO`). `orgId` tags every event with the school.
 */
export const initializeTrackingServices = async (orgId?: number | null) => {
  // `NEXT_PUBLIC_DD_ENV` / `NEXT_PUBLIC_DD_VERSION` are inlined at build time by next.config.js.
  const env = process.env.NEXT_PUBLIC_DD_ENV || process.env.NODE_ENV;
  const version = process.env.NEXT_PUBLIC_DD_VERSION || undefined;

  if (shouldRunAnalytics()) {
    if (window.isDDRumInitialized) return true;
    window.isDDLogsInitialized = true;
    window.isDDRumInitialized = true;

    datadogLogs.init({
      clientToken: process.env.NEXT_PUBLIC_DD_PUBLIC_KEY,
      site: 'us5.datadoghq.com',
      forwardErrorsToLogs: true,
      // Uncaught errors, console.error and console.warn. console.log/info stay in the browser.
      forwardConsoleLogs: ['error', 'warn'],
      sessionSampleRate: 100,
      env,
      version,
      service: 'middle-school',
      beforeSend: (event: LogsEvent): boolean => {
        try {
          if (
            includesGoguardian(event.error as object | null, 'stack') ||
            includesGoguardian(event.error as object | null, 'message') ||
            includesGoguardian(event.resource as object | null, 'url') ||
            includesGoguardian(event.action as object | null, 'target')
          ) {
            return false;
          }
        } catch (err) {
          console.warn('Errors inside beforeSend', err);
        }

        return true;
      }
    });
    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
      clientToken: process.env.NEXT_PUBLIC_DD_RUM_CLIENT_KEY,
      site: 'us5.datadoghq.com',
      service: 'middle-school',
      env,
      version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      beforeSend: (event: RumEvent): boolean => {
        try {
          if (
            includesGoguardian(event.error as object | null, 'stack') ||
            includesGoguardian(event.error as object | null, 'message') ||
            includesGoguardian(event.resource as object | null, 'url') ||
            includesGoguardian(event.action as object | null, 'target')
          ) {
            return false;
          }
        } catch (err) {
          console.warn('Errors inside beforeSend', err);
        }

        return true;
      }
    });
    if (orgId) {
      datadogRum.setGlobalContextProperty('org_id', orgId);
      datadogLogs.setGlobalContextProperty('org_id', orgId);
    }
    return true;
  }
  return false;
};

/**
 * Attaches the signed-in user to RUM sessions and browser logs, so staff can find a student's
 * Session Replay by id or name. Email is left out on purpose: it adds PII and nothing to search by.
 */
export function identifyTrackedUser(user: { id: number; name: string | null; role: string }) {
  if (typeof window === 'undefined' || !window.isDDRumInitialized) return;
  const trackedUser = { id: String(user.id), name: user.name ?? undefined, role: user.role };
  datadogRum.setUser(trackedUser);
  datadogLogs.setUser(trackedUser);
}

/** Link to the current session's Session Replay, attached to problem reports. Undefined when RUM is off. */
export function getSessionReplayLink(): string | undefined {
  if (typeof window === 'undefined' || !window.isDDRumInitialized) return undefined;
  try {
    return datadogRum.getSessionReplayLink();
  } catch {
    return undefined;
  }
}

export function Logger<ErrorValue, Context extends object = Record<string, string | number | boolean | null>>({
  message,
  object,
  error
}: {
  message: string;
  object?: Context;
  error: ErrorValue;
}) {
  if (shouldRunAnalytics()) {
    datadogLogs.logger.error(
      message,
      { ...object, project: 'Middle School' },
      error instanceof Error ? error : new Error(String(error))
    );
  } else {
    console.log(message, { ...object, project: 'Middle School' }, error);
    console.trace(error);
  }
}
